import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { schemesV2Api, schemesV2Utils } from "../../services/schemesV2Api";
import schemeDetailsData from "../../data/schemeDetails.json";


const RedeemSchemeDialog = ({ open, onClose, productDetail, setDrawerOpen, setSchemeDiscountAmount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [rates, setRates] = useState({});
  const [metalAmount, setMetalAmount] = useState(0);
  const [metalWeight, setMetalWeight] = useState(0);
  const [metalRate, setMetalRate] = useState(0);
  const [stoneAmount, setStoneAmount] = useState(0);
  const [makingCharges, setMakingCharges] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [gstPerc, setGstPerc] = useState(0);
  const [hallmarkCharge, setHallmarkCharge] = useState(0);
  const [rodiumCharge, setRodiumCharge] = useState(0);
  const [schemes, setSchemes] = useState([]);
  const [redeemedSchemes, setRedeemedSchemes] = useState([]);
  const [prematureModalOpen, setPrematureModalOpen] = useState(false);
  const [selectedSchemeForPremature, setSelectedSchemeForPremature] = useState(null);
  const [enrollmentDetails, setEnrollmentDetails] = useState({});
  const [loadingEnrollments, setLoadingEnrollments] = useState({});
  const [schemeDetailsModalOpen, setSchemeDetailsModalOpen] = useState(false);
  const [selectedSchemeForDetails, setSelectedSchemeForDetails] = useState(null);
  const [benefitBreakdownModalOpen, setBenefitBreakdownModalOpen] = useState(false);
  const [selectedSchemeForBenefitBreakdown, setSelectedSchemeForBenefitBreakdown] = useState(null);

  const navigate = useNavigate();
  const productId = productDetail?.id || 583;
  const jwtToken = localStorage.getItem("token");

  // Helper function to determine if a scheme allows multiple applications
  const isMultipleSchemeAllowed = (schemeName) => {
    // Allow multiple schemes for 10 months and 30 days schemes
    // Restrict 11 months schemes to only one
    return schemeName.includes('10-Month') || schemeName.includes('30-Day');
  };

  // Helper function to check if 11 months scheme is applied
  const hasElevenMonthScheme = () => {
    return redeemedSchemes.some(scheme => scheme.name.includes('11-Month'));
  };

  const handleRedeemScheme = (scheme) => {
    const alreadyRedeemed = redeemedSchemes.find((s) => s.uniqueId === scheme.uniqueId);
    if (!alreadyRedeemed) {
      // Check if progress is less than 100 (premature redemption)
      if (scheme.progress_percentage < 100) {
        setSelectedSchemeForPremature(scheme);
        setPrematureModalOpen(true);
      } else {
        // Check if multiple schemes are allowed for this scheme type
        if (isMultipleSchemeAllowed(scheme.name)) {
          // Allow multiple schemes - add to existing list
          setRedeemedSchemes(prev => [...prev, scheme]);
        } else {
          // Only allow one scheme at a time for 11 months schemes
          setRedeemedSchemes([scheme]);
        }
      }
    }
  };

  const handlePrematureGoAhead = () => {
    if (selectedSchemeForPremature) {
      // Check if multiple schemes are allowed for this scheme type
      if (isMultipleSchemeAllowed(selectedSchemeForPremature.name)) {
        // Allow multiple schemes - add to existing list
        setRedeemedSchemes(prev => [...prev, selectedSchemeForPremature]);
      } else {
        // Only allow one scheme at a time for 11 months schemes
        setRedeemedSchemes([selectedSchemeForPremature]);
      }
    }
    setPrematureModalOpen(false);
    setSelectedSchemeForPremature(null);
  };

  const handlePrematureGoBack = () => {
    setPrematureModalOpen(false);
    setSelectedSchemeForPremature(null);
  };

  const handleRemoveScheme = (schemeUniqueId) => {
    setRedeemedSchemes((prev) => prev.filter((s) => s.uniqueId !== schemeUniqueId));
  };

  // Generate payment schedule based on enrollment data
  const generatePaymentSchedule = (enrollment) => {
    if (!enrollment) {
      return [];
    }
    
    const schedule = [];
    const enrollmentDate = new Date(enrollment.enrollment_date);
    const installmentAmount = parseFloat(enrollment.instalment_amount || 0);
    const totalPeriods = enrollment.plan_progress?.total_periods || 0;
    const periodsPaid = enrollment.plan_progress?.periods_paid || 0;
    
    // Get payment history if available
    const paymentHistory = enrollment.payment_details?.payment_history || 
                          enrollment.paymentHistory || 
                          enrollment.payment_history || 
                          enrollment.payment_details || [];
    
    for (let i = 1; i <= totalPeriods; i++) {
      const payment = paymentHistory.find(p => 
        parseInt(p.installment_number) === i
      );
      
      let status = 'pending';
      let paymentDate = null;
      let goldRateOnPaymentDate = null;
      let goldGramsCredited = null;
      
      if (payment) {
        const dueDate = new Date(payment.due_date);
        
        if (payment.status === 'paid') {
          status = 'paid';
          if (payment.updated_at) {
            const utcDate = new Date(payment.updated_at);
            const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
            paymentDate = istDate.toISOString().replace('T', ' ').substring(0, 19);
          }
          goldRateOnPaymentDate = payment.gold_rate_on_payment;
          goldGramsCredited = payment.gold_grams_credited;
        } else if (payment.status === 'pending' && i === periodsPaid + 1) {
          status = 'next';
        }
        
        schedule.push({
          installmentNumber: i,
          dueDate: dueDate.toLocaleDateString('en-GB'),
          paymentDate: paymentDate,
          amount: parseFloat(payment.amount),
          status: status,
          goldRate: goldRateOnPaymentDate,
          goldGramsCredited: goldGramsCredited,
        });
      } else {
        const dueDate = new Date(enrollmentDate);
        dueDate.setMonth(dueDate.getMonth() + i - 1);
        
        if (i <= periodsPaid) {
          status = 'paid';
        } else if (i === periodsPaid + 1) {
          status = 'next';
        }
        
        schedule.push({
          installmentNumber: i,
          dueDate: dueDate.toLocaleDateString('en-GB'),
          paymentDate: paymentDate,
          amount: installmentAmount,
          status: status,
          goldRate: goldRateOnPaymentDate,
          goldGramsCredited: goldGramsCredited
        });
      }
    }
    
    return schedule;
  };

  // Fetch enrollment details when hovering over info icon
  const handleInfoHover = async (scheme) => {
    if (enrollmentDetails[scheme.db_id] || loadingEnrollments[scheme.db_id]) {
      return; // Already loaded or loading
    }

    setLoadingEnrollments(prev => ({ ...prev, [scheme.db_id]: true }));
    try {
      const userId = localStorage.getItem('user_id');
      const enrollmentData = await schemesV2Api.getEnrollmentDetails(scheme.db_id, userId);
      setEnrollmentDetails(prev => ({
        ...prev,
        [scheme.db_id]: enrollmentData.data || enrollmentData
      }));
    } catch (error) {
      console.error('Failed to fetch enrollment details:', error);
    } finally {
      setLoadingEnrollments(prev => ({ ...prev, [scheme.db_id]: false }));
    }
  };

  // Handle info icon click on mobile to open modal
  const handleInfoClick = (scheme) => {
    setSelectedSchemeForDetails(scheme);
    setSchemeDetailsModalOpen(true);
    handleInfoHover(scheme); // Fetch data if not already loaded
  };

  // Handle benefit breakdown icon click on mobile to open modal
  const handleBenefitBreakdownClick = (scheme) => {
    setSelectedSchemeForBenefitBreakdown(scheme);
    setBenefitBreakdownModalOpen(true);
  };

  // Component for benefit breakdown content (reusable for tooltip and modal)
  const BenefitBreakdownContent = ({ scheme, isInModal = false }) => {
    return (
      <Box sx={{ p: isInModal ? 0 : 1 }}>
        {/* Header with icon */}
        <Box display="flex" alignItems="center" mb={1.5}>
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              backgroundColor: isInModal ? 'rgba(163, 110, 41, 0.1)' : 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1
            }}
          >
            <InfoIcon sx={{ fontSize: '0.7rem', color: isInModal ? '#a36e29' : 'white' }} />
          </Box>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={isInModal ? '#a36e29' : 'white'}
            sx={{ fontSize: '0.85rem' }}
          >
            Scheme Benefits Breakdown
          </Typography>
        </Box>
        
        {scheme.benefit_breakdown && scheme.benefit_breakdown.length > 0 ? (
          <Box>
            {scheme.benefit_breakdown.map((benefit, idx) => (
              <Box 
                key={idx} 
                sx={{ 
                  mb: 1.5,
                  p: 1,
                  backgroundColor: isInModal ? '#f5f5f5' : 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  border: isInModal ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.2)',
                  '&:last-child': { mb: 0 }
                }}
              >
                <Typography 
                  variant="body2" 
                  fontWeight="600"
                  sx={{ 
                    fontSize: '0.8rem', 
                    color: isInModal ? 'text.primary' : 'white', 
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  {benefit.description}
                </Typography>
                {benefit.calculation && (
                  <Typography 
                    variant="body2" 
                    color={isInModal ? 'text.secondary' : 'rgba(255,255,255,0.7)'}
                    sx={{ 
                      fontSize: '0.7rem', 
                      mb: 0.5,
                      fontStyle: 'italic',
                      lineHeight: 1.2
                    }}
                  >
                    {benefit.calculation}
                  </Typography>
                )}
                {benefit.amount && benefit.amount !== "0.00" && (
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between"
                    sx={{
                      mt: 0.5,
                      p: 0.5,
                      backgroundColor: isInModal ? 'rgba(163, 110, 41, 0.1)' : 'rgba(163, 110, 41, 0.2)',
                      borderRadius: '4px',
                      border: `1px solid ${isInModal ? 'rgba(163, 110, 41, 0.3)' : 'rgba(163, 110, 41, 0.3)'}`
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      color={isInModal ? '#a36e29' : '#ffd700'}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      Benefit Amount
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      color={isInModal ? '#a36e29' : '#ffd700'}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      ₹{Number(benefit.amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
            
            {/* Total summary */}
            <Box 
              sx={{ 
                mt: 1.5,
                p: 1,
                backgroundColor: isInModal ? '#fff3e0' : 'rgba(255, 215, 0, 0.15)',
                borderRadius: '8px',
                border: `1px solid ${isInModal ? '#ffb74d' : 'rgba(255, 215, 0, 0.3)'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                color={isInModal ? '#8B5A1F' : '#ffd700'}
                sx={{ fontSize: '0.8rem' }}
              >
                Total Benefits
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                color={isInModal ? '#8B5A1F' : '#ffd700'}
                sx={{ fontSize: '0.9rem' }}
              >
                ₹{Number(scheme.raw_benefit || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box 
            sx={{ 
              p: 1.5,
              backgroundColor: isInModal ? '#f5f5f5' : 'rgba(255,255,255,0.1)',
              borderRadius: '8px',
              border: isInModal ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.2)',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="body2" 
              color={isInModal ? 'text.secondary' : 'rgba(255,255,255,0.7)'}
              sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}
            >
              No benefit details available
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Get status chip
  const getStatusChip = (status) => {
    const colors = {
      paid: { bg: '#4caf50', color: 'white' },
      next: { bg: '#ff9800', color: 'white' },
      pending: { bg: '#e0e0e0', color: '#666' }
    };
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        sx={{
          backgroundColor: colors[status]?.bg || colors.pending.bg,
          color: colors[status]?.color || colors.pending.color,
          fontWeight: 600,
          fontSize: "10px"
        }}
      />
    );
  };

  // Tooltip content component
  const SchemeDetailsTooltip = ({ scheme }) => {
    const enrollment = enrollmentDetails[scheme.db_id];
    const schemeId = enrollment?.scheme_id || scheme.id || '';
    
    // Find scheme details from JSON data
    const schemeDetails = schemeDetailsData.schemes.find(s => s.scheme_id === schemeId);
    
    const isDailyScheme = schemeId === '30-day-daily-savings';
    const isAccumulationScheme = schemeId === '10-month-accumulation';
    const isDirectPurchaseScheme = schemeId === '11-month-direct-purchase';

    // Use JSON data if available, otherwise fall back to enrollment data
    const displayData = schemeDetails || enrollment;
    const monthlyBreakdown = schemeDetails?.monthly_breakdown || enrollment?.monthly_breakdown;
    const benefits = schemeDetails?.benefits || enrollment?.benefits;

    return (
      <Box sx={{ maxWidth: 700, p: 2, maxHeight: '80vh', overflowY: 'auto' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
          {scheme.name} - Details
        </Typography>
        {loadingEnrollments[scheme.db_id] ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#A36E29', mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#A36E29', fontFamily: '"Roboto", sans-serif' }}>Loading scheme details...</Typography>
          </Box>
        ) : displayData ? (
          <Box>
            {/* Scheme Description */}
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', fontFamily: '"Roboto", sans-serif' }}>
                {displayData.description || (isDailyScheme && "Pay daily for 30 days. Get a bonus equal to 100% of one day's instalment.") || 
                (isAccumulationScheme && "Save monthly for 10 months and get a bonus of 50% on completion. Perfect for building wealth gradually.") ||
                (isDirectPurchaseScheme && "Pay monthly for 11 months. On completion, we waive 100% of the making charge on your chosen jewellery.")}
              </Typography>
            </Box>

            {/* Monthly Breakdown */}
            {isDailyScheme && monthlyBreakdown && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                  Payment Schedule
                </Typography>
                {monthlyBreakdown.map((month, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#A36E29', fontFamily: '"Roboto", sans-serif' }}>
                      {month.month}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Day</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Due Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {month.days?.map((day, dayIdx) => (
                            <TableRow key={dayIdx}>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>{day.day}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>{day.due_date}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>₹{day.instalment_amount?.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                      <Typography variant="caption">Total Paid: ₹{month.total_paid?.toLocaleString()}</Typography>
                      <Typography variant="caption">Cumulative: ₹{month.cumulative_paid?.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {(isAccumulationScheme || isDirectPurchaseScheme) && monthlyBreakdown && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif' }}>
                  Payment Schedule
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Month</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Amount</TableCell>
                        {isAccumulationScheme && (
                          <>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Gold Rate</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Gold (g)</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Cumulative (g)</TableCell>
                          </>
                        )}
                        {isDirectPurchaseScheme && (
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Waiver</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '4px 8px' }}>Cumulative Paid</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyBreakdown.map((month, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>{month.month_name || `Month ${month.month}`}</TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>{month.due_date}</TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>₹{month.instalment_amount?.toLocaleString()}</TableCell>
                          {isAccumulationScheme && (
                            <>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>
                                {month.gold_rate_on_payment ? `₹${month.gold_rate_on_payment.toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px', color: '#A36E29' }}>
                                {month.gold_grams_purchased ? `${parseFloat(month.gold_grams_purchased).toFixed(3)}g` : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px', color: '#A36E29', fontWeight: 600 }}>
                                {month.cumulative_grams ? `${parseFloat(month.cumulative_grams).toFixed(3)}g` : '-'}
                              </TableCell>
                            </>
                          )}
                          {isDirectPurchaseScheme && (
                            <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px', color: '#2e7d32', fontWeight: 600 }}>
                              {month.making_charge_waiver || '0%'}
                            </TableCell>
                          )}
                          <TableCell sx={{ fontSize: '0.7rem', padding: '4px 8px' }}>₹{month.cumulative_paid?.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Benefits Summary */}
            {benefits && (
              <Box sx={{ mt: 2, mb: 2, p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Roboto", sans-serif', color: '#8B5A1F' }}>
                  Benefits
                </Typography>
                {benefits.on_completion && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#8B5A1F', fontSize: '0.8rem' }}>
                      On Completion ({benefits.on_completion.required_payments || benefits.on_completion.required_months} {isDailyScheme ? 'days' : 'months'}):
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', ml: 1, color: '#8B5A1F' }}>
                      • {benefits.on_completion.benefit_description}
                    </Typography>
                    {benefits.on_completion.benefit_amount && !isDirectPurchaseScheme && (
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', ml: 1, color: '#2e7d32', fontWeight: 600 }}>
                        • Amount: ₹{typeof benefits.on_completion.benefit_amount === 'number' ? benefits.on_completion.benefit_amount.toLocaleString() : benefits.on_completion.benefit_amount}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ p: 2 }}>No scheme details available</Typography>
        )}
      </Box>
    );
  };

  const totalBenefitAmount = Number(redeemedSchemes.reduce(
    (acc, curr) => acc + (curr.raw_benefit || 0),
    0
  ).toFixed(2));

  // Update parent component's scheme discount amount
  useEffect(() => {
    if (setSchemeDiscountAmount) {
      setSchemeDiscountAmount(totalBenefitAmount);
    }
    
    // Store scheme information in localStorage for buyNow function
    if (redeemedSchemes.length > 0) {
      // For multiple schemes, store as comma-separated values
      const schemeIds = redeemedSchemes.map(scheme => scheme.name).join(',');
      const schemeDbIds = redeemedSchemes.map(scheme => scheme.db_id).join(',');
      
      localStorage.setItem("appliedSchemeId", schemeIds);
      localStorage.setItem("appliedSchemeDbId", schemeDbIds);
    } else {
      localStorage.removeItem("appliedSchemeId");
      localStorage.removeItem("appliedSchemeDbId");
    }
  }, [redeemedSchemes, setSchemeDiscountAmount, totalBenefitAmount]);

  useEffect(() => {
    console.log("🔍 FETCHING GOLD RATES FROM API...");
    fetch(`${process.env.REACT_APP_API_URL}/v1.0.0/user/landing.php`)
      .then((res) => res.json())
      .then((data) => {
        console.log("📊 LANDING API RESPONSE:", data);
        console.log("💰 JEWELLERY INVENTORY RATES:", data?.response?.jewellery_inventory);
        setRates(data?.response?.jewellery_inventory || {});
      })
      .catch((err) => {
        console.error("❌ Failed to fetch rates", err);
        setRates({});
      });
  }, []);

  useEffect(() => {
    if (open && productId) {
      const userId = localStorage.getItem("user_id");
      schemesV2Api.getSchemeBenefits(productId, userId)
        .then((data) => {
          if (data.success && Array.isArray(data.response)) {
            const parsedSchemes = data.response.map((schemeItem, index) => {
              const benefit = parseFloat(schemeItem.redeemable_amount || "0");
              const schemeDetails = schemeItem.scheme_details;
              const parsedBenefits = JSON.parse(schemeDetails.benefits || "[]");

              return {
                db_id: schemeItem.enrollment_id,
                id: schemeDetails.id,
                uniqueId: `${schemeDetails.id}_${benefit}_${index}`, // Create unique identifier
                name: schemeDetails.name,
                value: `₹${benefit.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}`,
                valid_till: schemeItem.exp_closure_date,
                benefits: parsedBenefits,
                raw_benefit: benefit,
                progress_percentage: schemeItem.progress_percentage,
                total_paid: parseFloat(schemeItem.total_paid || "0"), // Add total paid amount
                benefit_breakdown: schemeItem.benefit_breakdown || [], // Add benefit breakdown data
              };
            });

            setSchemes(parsedSchemes);
          } else {
            setRedeemedSchemes([]);
            setSchemes([]);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch scheme data", err);
          setRedeemedSchemes([]);
          setSchemes([]);
        });
    }
  }, [open, productId, jwtToken]);

  useEffect(() => {
    const metalInfo = productDetail?.customizations?.variants?.options?.[0]?.metal_info || {};
    if (!metalInfo || !rates || !metalInfo.quality) return;

    const rate = Number((rates[metalInfo.quality] || 0).toFixed(2));
    const grossWt = parseFloat(metalInfo.gross_wt || 0);
    const stoneWt = parseFloat(metalInfo.stone_wt || 0);
    const netWeight = grossWt - stoneWt;
    const wastagePerc = parseFloat(metalInfo.wastage_prec || 0);
    const wastageWeight = netWeight * (wastagePerc / 100);
    const netWeightAfterWastage = netWeight + wastageWeight;

    // Calculate metal value only (for GST calculation)
    const metalValueOnly = Number((netWeightAfterWastage * rate).toFixed(2));
    
    // Store weight and rate for display
    setMetalWeight(netWeightAfterWastage);
    setMetalRate(rate);
    
    // Calculate making charges
    const makingChargeValue = Number((parseFloat(metalInfo.making_charge_value || 0)).toFixed(2));
    const makingChargeAmount = Number(((metalValueOnly * makingChargeValue) / 100).toFixed(2));

    // 🔍 DEBUGGING: Making Charge Calculation
    console.log("🔍 MAKING CHARGE CALCULATION DEBUG:");
    console.log("📊 API Data Sources:");
    console.log("- Rates from /v1.0.0/user/landing.php:", rates);
    console.log("- Metal Info from product details:", metalInfo);
    console.log("📏 Weight Calculations:");
    console.log("- Gross Weight:", grossWt, "g");
    console.log("- Stone Weight:", stoneWt, "g");
    console.log("- Net Weight:", netWeight, "g");
    console.log("- Wastage %:", wastagePerc, "%");
    console.log("- Wastage Weight:", wastageWeight, "g");
    console.log("- Net Weight After Wastage:", netWeightAfterWastage, "g");
    console.log("🔧 FIXED: Using full precision (no early rounding)");
    console.log("💰 Rate & Value Calculations:");
    console.log("- Metal Quality:", metalInfo.quality);
    console.log("- Current Gold Rate:", rate, "₹/g");
    console.log("- Metal Value Only:", metalValueOnly, "₹");
    console.log("⚙️ Making Charge Details:");
    console.log("- Making Charge %:", makingChargeValue, "%");
    console.log("- Making Charge Amount:", makingChargeAmount, "₹");
    console.log("- Calculation: (", metalValueOnly, "×", makingChargeValue, ") ÷ 100 =", makingChargeAmount);
    console.log("🎯 Expected Backend Values:");
    console.log("- Backend Gold Rate: ₹10,000.00");
    console.log("- Backend Net Weight: 4.216g");
    console.log("- Backend Making Charge %: 18.99%");
    console.log("- Backend Total: ₹8,246.37");
    console.log("🔍 Frontend vs Backend Comparison:");
    console.log("- Rate Match:", rate === 10000 ? "✅ YES" : `❌ NO (Frontend: ${rate}, Backend: 10000)`);
    console.log("- Weight Match:", netWeightAfterWastage === 4.216 ? "✅ YES" : `❌ NO (Frontend: ${netWeightAfterWastage}, Backend: 4.216)`);
    console.log("- Making Charge % Match:", makingChargeValue === 18.99 ? "✅ YES" : `❌ NO (Frontend: ${makingChargeValue}, Backend: 18.99)`);
    console.log("=".repeat(80));
    
    // Additional charges
    const hallmark = Number((parseFloat(metalInfo.hallmark_charge || 0)).toFixed(2));
    const rodium = Number((parseFloat(metalInfo.rodium_charge || 0)).toFixed(2));
    
    // Calculate GST only on metal value (excluding making charges) - matching drawer logic
    const gstPercent = Number((parseFloat(metalInfo.gst_perc || 0)).toFixed(2));
    const metalGst = Number(((metalValueOnly * gstPercent) / 100).toFixed(2));
    
    // Calculate stone GST separately if stone info exists
    const stoneInfo = productDetail?.customizations?.variants?.options[0]?.stone_info;
    let stoneGst = 0;
    let stoneBaseAmount = 0;
    if (stoneInfo) {
      stoneBaseAmount = Number((parseFloat(stoneInfo.stone_rate || 0) * parseFloat(stoneInfo.carat || 0) * 0.2).toFixed(2));
      const stoneGstPerc = Number((parseFloat(stoneInfo.gst_perc || 0)).toFixed(2));
      stoneGst = Number(((stoneBaseAmount * stoneGstPerc) / 100).toFixed(2));
    }
    
    // Total GST (metal + stone)
    const totalGst = Number((metalGst + stoneGst).toFixed(2));

    setMetalAmount(metalValueOnly);
    setStoneAmount(stoneBaseAmount); // Use calculated stone amount instead of stone_amount from metal info
    setMakingCharges(makingChargeAmount);
    setHallmarkCharge(hallmark);
    setRodiumCharge(rodium);
    setGstAmount(totalGst);
    setGstPerc(gstPercent);
  }, [rates, productDetail]);

  const buyNow = async () => {
    const userId = localStorage.getItem("user_id")
      ? localStorage.getItem("user_id")
      : -1;

    if (userId !== -1) {
      // Handle multiple schemes by passing comma-separated values
      const schemeIds = redeemedSchemes.length > 0 
        ? redeemedSchemes.map(scheme => scheme.name).join(',')
        : null;
      const schemeDbIds = redeemedSchemes.length > 0 
        ? redeemedSchemes.map(scheme => scheme.db_id).join(',')
        : null;

      navigate(
        `/checkout?action=buy-now&prod=${productDetail?.name}&hash=${
          productDetail?.hash
        }&customization=${
          productDetail?.customizations?.variants?.options[0]?.id || -1
        }&discount=${0}&coins=${0}&schemeId=${schemeIds}&db_id=${schemeDbIds}`
      );
      onClose(); // Close the dialog
    } else {
      navigate(
        `/signin?redirect_to=/item/${productDetail?.category}/${productDetail?.name}-${productDetail?.hash}`
      );
    }
  };

  const handleGoBackToDrawer = () => {
    onClose(); // Close the dialog
    // Drawer remains open, no need to reopen it
  };

  const totalAmount = Number((
    metalAmount +           // Metal value only
    makingCharges +         // Making charges
    hallmarkCharge +        // Hallmark charges
    rodiumCharge +          // Rodium charges
    stoneAmount +           // Stone amount (calculated from stone info)
    gstAmount -             // Total GST (metal + stone)
    totalBenefitAmount      // Scheme benefit deduction
  ).toFixed(2));

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isMobile ? "sm" : "md"}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: isMobile ? "95vh" : "90vh",
          display: "flex",
          flexDirection: "column",
          margin: isMobile ? "16px" : "32px",
          borderRadius: isMobile ? "12px" : "8px"
        }
      }}
    >
      <Box p={isMobile ? 1 : 2}>
        <Typography 
          variant={isMobile ? "h6" : "h6"} 
          align="center" 
          fontWeight="bold" 
          gutterBottom
          sx={{ fontSize: isMobile ? "1.1rem" : "1.25rem" }}
        >
          Redeem My Schemes
        </Typography>

        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          px={isMobile ? 1 : 2}
          py={1}
          borderBottom="1px solid #e0e0e0"
          gap={isMobile ? 1 : 0}
        >
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Selected Product: <strong>Gold Ring</strong>
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Product Value:{" "}
            <strong>
              ₹{" "}
              {Number(
                metalAmount +
                  stoneAmount +
                  makingCharges +
                  hallmarkCharge +
                  rodiumCharge +
                  gstAmount
              ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </strong>
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Net Payable Amount:{" "}
            <strong style={{ color: "#a36e29" }}>
              ₹{" "}
              {Number(totalAmount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </strong>
          </Typography>
        </Box>

        <DialogContent sx={{ 
          flex: 1, 
          overflowY: "auto", 
          padding: isMobile ? "12px" : "16px",
          paddingBottom: isMobile ? "80px" : "16px"
        }}>
          
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              {schemes.map((scheme, index) => {
                console.log( "scheme in redeem dialog", scheme);
                const isRedeemed = redeemedSchemes.find(
                  (s) => s.uniqueId === scheme.uniqueId
                );
                
                // Check if this scheme allows multiple applications
                const allowsMultiple = isMultipleSchemeAllowed(scheme.name);
                const isElevenMonthScheme = scheme.name.includes('11-Month');
                
                // Determine if this scheme should be disabled
                let isDisabled = false;
                
                if (isRedeemed) {
                  // Never disable a scheme that's already applied
                  isDisabled = false;
                } else if (isElevenMonthScheme) {
                  // 11 months schemes are disabled if any other scheme is applied
                  isDisabled = redeemedSchemes.length > 0;
                } else if (allowsMultiple) {
                  // 10 months and 30 days schemes are disabled if 11 months scheme is applied
                  isDisabled = hasElevenMonthScheme();
                } else {
                  // For any other scheme types, disable if any scheme is applied
                  isDisabled = redeemedSchemes.length > 0;
                }
                
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      mb: isMobile ? 1 : 2,
                      borderRadius: "10px",
                      overflow: "hidden",
                      boxShadow: 1,
                      opacity: isDisabled ? 0.6 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "40px" : "50px",
                        background:
                          "linear-gradient(to bottom, #e0c08d, #a36e29)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          transform: "rotate(-90deg)",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: isMobile ? "0.65rem" : "0.75rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Scheme ID - {scheme.db_id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        backgroundColor: "#fff",
                        p: isMobile ? 1.5 : 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography 
                            fontWeight="bold" 
                            sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                          >
                            {scheme.name}
                          </Typography>
                        </Box>
                        <Typography 
                          fontSize={isMobile ? "0.75rem" : "0.85rem"} 
                          color="text.secondary"
                        >
                          Valid till {scheme.valid_till}
                        </Typography>
                        <Typography
                          mt={1}
                          fontWeight="bold"
                          sx={{ 
                            color: "#b27900",
                            fontSize: isMobile ? "0.8rem" : "1rem"
                          }}
                        >
                          {scheme.value}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {isMobile ? (
                          <IconButton
                            size="small"
                            onClick={() => handleInfoClick(scheme)}
                            disabled={isDisabled}
                            sx={{
                              color: isDisabled ? "#ccc" : "#a36e29",
                              "&:hover": {
                                backgroundColor: isDisabled ? "transparent" : "rgba(163, 110, 41, 0.08)"
                              }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <Tooltip
                            title={<SchemeDetailsTooltip scheme={scheme} />}
                            arrow
                            placement="left"
                            disableInteractive={false}
                            enterDelay={300}
                            leaveDelay={200}
                            onOpen={() => handleInfoHover(scheme)}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 'none',
                                  maxHeight: '85vh',
                                  backgroundColor: 'white',
                                  border: '1px solid #e0e0e0',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                  overflow: 'visible',
                                  '& .MuiTooltip-arrow': {
                                    color: 'white',
                                    '&::before': {
                                      border: '1px solid #e0e0e0',
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                color: isDisabled ? "#ccc" : "#a36e29",
                                "&:hover": {
                                  backgroundColor: isDisabled ? "transparent" : "rgba(163, 110, 41, 0.08)"
                                }
                              }}
                              onMouseEnter={() => handleInfoHover(scheme)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isRedeemed ? (
                          <Button
                            variant="text"
                            sx={{ 
                              color: "#d32f2f", 
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.04)"
                              }
                            }}
                            onClick={() => handleRemoveScheme(scheme.uniqueId)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            variant="text"
                            disabled={isDisabled}
                            sx={{ 
                              color: isDisabled ? "#ccc" : "#a36e29", 
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: isDisabled ? "transparent" : "rgba(163, 110, 41, 0.04)"
                              }
                            }}
                            onClick={() => handleRedeemScheme(scheme)}
                          >
                            Apply
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ 
                  p: isMobile ? 1.5 : 2, 
                  borderRadius: "8px", 
                  backgroundColor: "#fafafa" 
                }}
              >
                <Typography 
                  fontWeight="bold" 
                  mb={isMobile ? 1.5 : 2}
                  sx={{ fontSize: isMobile ? "0.95rem" : "1rem" }}
                >
                  Price Breakout Details
                </Typography>
                <Divider />

                {[
                  { label: "Metal Amount", value: metalAmount, weight: metalWeight, rate: metalRate, showBreakdown: true },
                  { label: "Stone Amount", value: stoneAmount },
                  { label: "Making Charges", value: makingCharges },
                  { label: "Hallmark Charge", value: hallmarkCharge },
                  { label: "Rodium Charge", value: rodiumCharge },
                  { label: `GST (${gstPerc}%)`, value: gstAmount },
                ].map((item, i) => (
                  <Box key={i} my={isMobile ? 0.5 : 1}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography sx={{ fontSize: isMobile ? "0.8rem" : "1rem" }}>
                          {item.label}
                        </Typography>
                        {item.showBreakdown && item.weight > 0 && item.rate > 0 && (
                          <Typography 
                            sx={{ 
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              color: "text.secondary",
                              mt: 0.25
                            }}
                          >
                            {productDetail?.customizations?.variants?.options?.[0]?.metal_info?.metal?.toLowerCase() === 'silver'
                              ? item.weight.toFixed(2)
                              : item.weight.toFixed(3)
                            }g × ₹{item.rate.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        )}
                      </Box>
                      <Typography 
                        fontWeight="bold" 
                        sx={{ 
                          color: item.isDiscount ? "#2e7d32" : "#a36e29",
                          fontSize: isMobile ? "0.8rem" : "1rem"
                        }}
                      >
                        {item.isDiscount && item.value > 0 ? "- " : ""}₹{" "}
                        {Number(item.value).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {/* TOTAL PRODUCT VALUE */}
                <Divider sx={{ my: isMobile ? 1 : 1.5, borderColor: '#777' }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  my={isMobile ? 1 : 1.5}
                >
                <Typography sx={{ fontSize: isMobile ? "0.9rem" : "1rem", fontWeight: "bold" }}>
                  Total Product Value
                </Typography>
                <Typography 
                  fontWeight="bold" 
                  sx={{ 
                    color: "black",
                    fontSize: isMobile ? "0.9rem" : "1rem"
                  }}
                >
                ₹{" "}
                {Number(
                  metalAmount +
                  stoneAmount +
                  makingCharges +
                  hallmarkCharge +
                  rodiumCharge +
                  gstAmount
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            
                {/* Scheme Benefits Breakdown */}
                {redeemedSchemes.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography 
                      fontWeight="bold" 
                      mb={1}
                      sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "#2e7d32" }}
                    >
                      Scheme Benefits Applied
                    </Typography>
                    {redeemedSchemes.map((scheme, index) => {
                      // Calculate invested amount (total_paid from scheme data)
                      const investedAmount = scheme.total_paid || 0;
                      // Calculate additional benefits (redeemable_amount - invested_amount)
                      const additionalBenefits = (scheme.raw_benefit || 0) - investedAmount;
                      
                      // Debug: Log scheme data to see what's available
                      console.log("Scheme data for tooltip:", scheme);
                      
                      return (
                        <Box key={index} sx={{ mb: 1.5 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            sx={{ fontSize: isMobile ? "0.8rem" : "0.9rem", color: "#2e7d32" }}
                          >
                            {scheme.name}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" my={0.5}>
                            <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.85rem" }}>
                              Your Investment
                            </Typography>
                            <Typography 
                              fontWeight="bold" 
                              sx={{ 
                                color: "#2e7d32",
                                fontSize: isMobile ? "0.75rem" : "0.85rem"
                              }}
                            >
                              ₹{Number(investedAmount).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" my={0.5}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.85rem" }}>
                                Scheme Bonus
                              </Typography>
                              {isMobile ? (
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleBenefitBreakdownClick(scheme)}
                                  sx={{ 
                                    p: 0.5,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(163, 110, 41, 0.1)',
                                    border: '1px solid rgba(163, 110, 41, 0.3)',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: 'rgba(163, 110, 41, 0.2)',
                                      borderColor: 'rgba(163, 110, 41, 0.5)',
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 2px 8px rgba(163, 110, 41, 0.3)'
                                    }
                                  }}
                                >
                                  <InfoIcon sx={{ 
                                    fontSize: "0.8rem", 
                                    color: "#a36e29",
                                    transition: 'color 0.2s ease-in-out'
                                  }} />
                                </IconButton>
                              ) : (
                                <Tooltip 
                                  title={<BenefitBreakdownContent scheme={scheme} />}
                                  arrow
                                  placement="top"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: 'linear-gradient(135deg, #2d1b00 0%, #4a2c00 50%, #6b3d00 100%)',
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        padding: 0,
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 32px rgba(255, 215, 0, 0.2), 0 2px 8px rgba(0,0,0,0.3)',
                                        maxWidth: 400,
                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                        backdropFilter: 'blur(10px)',
                                        '& .MuiTooltip-arrow': {
                                          color: '#2d1b00',
                                          filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3))'
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <IconButton 
                                    size="small" 
                                    sx={{ 
                                      p: 0.5,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(163, 110, 41, 0.1)',
                                      border: '1px solid rgba(163, 110, 41, 0.3)',
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        backgroundColor: 'rgba(163, 110, 41, 0.2)',
                                        borderColor: 'rgba(163, 110, 41, 0.5)',
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 2px 8px rgba(163, 110, 41, 0.3)'
                                      }
                                    }}
                                  >
                                    <InfoIcon sx={{ 
                                      fontSize: "0.9rem", 
                                      color: "#a36e29",
                                      transition: 'color 0.2s ease-in-out'
                                    }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Typography 
                              fontWeight="bold" 
                              sx={{ 
                                color: "#2e7d32",
                                fontSize: isMobile ? "0.75rem" : "0.85rem"
                              }}
                            >
                              ₹{Number(additionalBenefits).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" my={0.5}>
                            <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.85rem" }}>
                              Total Scheme Value
                            </Typography>
                            <Typography 
                              fontWeight="bold" 
                              sx={{ 
                                color: "#2e7d32",
                                fontSize: isMobile ? "0.75rem" : "0.85rem"
                              }}
                            >
                              ₹{Number(scheme.raw_benefit || 0).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" my={1}>
                      <Typography 
                        fontWeight="bold"
                        sx={{ fontSize: isMobile ? "0.85rem" : "0.95rem" }}
                      >
                        Total Scheme Discount
                      </Typography>
                      <Typography 
                        fontWeight="bold" 
                        sx={{ 
                          color: "#2e7d32",
                          fontSize: isMobile ? "0.85rem" : "0.95rem"
                        }}
                      >
                        - ₹{Number(totalBenefitAmount).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />
                <Box display="flex" justifyContent="space-between" mt={isMobile ? 1.5 : 2}>
                  <Typography 
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                  >
                    Net Payable Amount
                  </Typography>
                  <Typography 
                    fontWeight="bold" 
                    sx={{ 
                      color: "#a36e29",
                      fontSize: isMobile ? "0.9rem" : "1rem"
                    }}
                  >
                    ₹{" "}
                    {Number(totalAmount).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions 
          sx={{ 
            justifyContent: "space-between", 
            px: isMobile ? 2 : 3, 
            pb: isMobile ? 1 : 2,
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e0e0e0",
            zIndex: 1,
            margin: 0,
            padding: isMobile ? "16px 0px" : "20px 0px"
          }}
        >
          <Box sx={{ display: "flex", gap: isMobile ? 1 : 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "#a36e29",
                color: "#a36e29",
                fontWeight: 500,
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px 16px" : "10px 20px",
              }}
            >
              Cancel
            </Button>
          <Button
            variant="contained"
            // onClick={async () => {
            //   try {
            //     const token = localStorage.getItem("token");
            //     const userId = localStorage.getItem("user_id");

            //     if (!token || !userId) {
            //       alert("Please log in to place the order.");
            //       return;
            //     }

            //     // Prepare ordered products array
            //     const orderedProducts = [
            //       {
            //         product_id: productDetail?.id,
            //         customization_id:
            //           productDetail?.customizations?.variants?.options[0]?.id || -1,
            //         quantity: 1,
            //         customizations_requested: null,
            //       },
            //     ];

            //     // Prepare order request payload matching backend expectations
            //     const orderPayload = {
            //       type: "order_request",
            //       currency: "INR",
            //       receipt: `receipt_${Date.now()}`,
            //       user_address_id: null, // TODO: set user address id if available
            //       payment_status: "created",
            //       coupon_id: null,
            //       wallet_amount: 0,
            //       ordered_products: JSON.stringify(orderedProducts),
            //       scheme_id: redeemedSchemes.length > 0 ? redeemedSchemes[0].id : null,
            //       scheme_amount: totalBenefitAmount.toFixed(2),
            //     };

            //     const params = new URLSearchParams();
            //     for (const key in orderPayload) {
            //       params.append(key, orderPayload[key]);
            //     }

            //     const { data } = await axios.post(
            //       `${process.env.REACT_APP_API_URL}/v1.0.0/user/products/payment.php`,
            //       params,
            //       {
            //         headers: {
            //           Authorization: `Bearer ${token}`,
            //           "Content-Type": "application/x-www-form-urlencoded",
            //         },
            //       }
            //     );

            //     if (data.success) {
            //       // Call Razorpay payment flow with order details
            //       await handlePayment(data.response);
            //     } else {
            //       alert("Failed to place the order.");
            //     }
            //   } catch (err) {
            //     console.error("Order submission failed", err);
            //     alert("Something went wrong. Please try again.");
            //   }
            // }}
            onClick={buyNow}
            sx={{
              background: "linear-gradient(to right, #d4a76a, #a36e29)",
              color: "white",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px 16px" : "10px 20px",
            }}
          >
            Redeem Now
          </Button>
          </Box>
        </DialogActions>
      </Box>

      {/* Premature Redemption Modal */}
      <Dialog
        open={prematureModalOpen}
        onClose={handlePrematureGoBack}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            margin: "16px"
          }
        }}
      >
        <Box p={3}>
          <Typography 
            variant="h6" 
            align="center" 
            fontWeight="bold" 
            gutterBottom
            sx={{ color: "#d32f2f", mb: 2 }}
          >
            Premature Redemption
          </Typography>
          
          <Typography 
            variant="body1" 
            align="center" 
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            This scheme is only <strong>{selectedSchemeForPremature?.progress_percentage}%</strong> complete. 
            Redeeming now means you'll lose the completion bonus and other benefits.
          </Typography>

          {selectedSchemeForPremature && (
            <Box 
              sx={{ 
                backgroundColor: "#f5f5f5", 
                p: 2, 
                borderRadius: "8px", 
                mb: 3 
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Scheme Details:</strong>
              </Typography>
              <Typography variant="body2">
                • Scheme: {selectedSchemeForPremature.name}
              </Typography>
              <Typography variant="body2">
                • Redeemable Amount: {selectedSchemeForPremature.value}
              </Typography>
              <Typography variant="body2">
                • Progress: {selectedSchemeForPremature.progress_percentage}%
              </Typography>
            </Box>
          )}

          <Typography 
            variant="body2" 
            align="center" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Do you want to proceed with the premature redemption?
          </Typography>

          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              onClick={handlePrematureGoBack}
              variant="outlined"
              sx={{
                borderColor: "#a36e29",
                color: "#a36e29",
                fontWeight: 500,
                px: 3,
                py: 1,
                "&:hover": {
                  borderColor: "#a36e29",
                  backgroundColor: "rgba(163, 110, 41, 0.04)"
                }
              }}
            >
              Go Back
            </Button>
            <Button
              onClick={handlePrematureGoAhead}
              variant="contained"
              sx={{
                background: "linear-gradient(to right, #d4a76a, #a36e29)",
                color: "white",
                fontWeight: "bold",
                px: 3,
                py: 1,
                "&:hover": {
                  background: "linear-gradient(to right, #c19a5a, #925d20)"
                }
              }}
            >
              Go Ahead
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Scheme Details Modal for Mobile */}
      <Dialog
        open={schemeDetailsModalOpen}
        onClose={() => setSchemeDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: isMobile ? 0 : "12px",
            margin: isMobile ? 0 : "16px",
            maxHeight: isMobile ? "100vh" : "90vh"
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
              {selectedSchemeForDetails?.name || 'Scheme Details'}
            </Typography>
            <IconButton
              onClick={() => setSchemeDetailsModalOpen(false)}
              sx={{
                color: '#a36e29'
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: isMobile ? 2 : 3, maxHeight: isMobile ? 'calc(100vh - 80px)' : 'calc(90vh - 80px)', overflowY: 'auto' }}>
            {selectedSchemeForDetails && (
              <SchemeDetailsTooltip scheme={selectedSchemeForDetails} />
            )}
          </DialogContent>
        </Box>
      </Dialog>

      {/* Benefit Breakdown Modal for Mobile */}
      <Dialog
        open={benefitBreakdownModalOpen}
        onClose={() => setBenefitBreakdownModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: isMobile ? 0 : "12px",
            margin: isMobile ? 0 : "16px",
            maxHeight: isMobile ? "100vh" : "90vh"
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
              {selectedSchemeForBenefitBreakdown?.name || 'Scheme Benefits Breakdown'}
            </Typography>
            <IconButton
              onClick={() => setBenefitBreakdownModalOpen(false)}
              sx={{
                color: '#a36e29'
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: isMobile ? 2 : 3, maxHeight: isMobile ? 'calc(100vh - 80px)' : 'calc(90vh - 80px)', overflowY: 'auto' }}>
            {selectedSchemeForBenefitBreakdown && (
              <BenefitBreakdownContent scheme={selectedSchemeForBenefitBreakdown} isInModal={true} />
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default RedeemSchemeDialog;
